import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormControl, FormLabel, InputGroup } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { HexAlphaColorPicker } from "react-colorful";
import { IComponentProperty } from "../../../models/Interfaces";
import styles from "./ColourPicker.module.scss";

interface IStandaloneColourPickerProps {
  selectedColour?: string | null | undefined;
  onColourChange?: (selectedColour: string | null) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  property?: never;
  onValueChange?: never;
  onEditorOpen?: never;
  editorHeight?: never;
}

interface IPropertyColourPickerProps {
  property: IComponentProperty;
  onValueChange: (propertyid: number, value: string) => void;
  selectedColour?: never;
  onColourChange?: never;
  onInputChange?: never;
  name?: never;
  id?: never;
}

type IColourPickerProps = IStandaloneColourPickerProps | IPropertyColourPickerProps;

const DEFAULT_PICKER_COLOUR = "#ffffffff";

const isPropertyPicker = (props: IColourPickerProps): props is IPropertyColourPickerProps => {
  return "property" in props && props.property !== undefined;
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const toHex = (value: number): string => {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
};

const normaliseHexColour = (value: string): string | null => {
  const trimmed = value.trim();

  if (!trimmed.startsWith("#")) {
    return null;
  }

  const hex = trimmed.slice(1);

  if (hex.length === 3) {
    return `#${hex.split("").map((char) => `${char}${char}`).join("")}ff`;
  }

  if (hex.length === 4) {
    return `#${hex.split("").map((char) => `${char}${char}`).join("")}`;
  }

  if (hex.length === 6) {
    return `#${hex}ff`;
  }

  if (hex.length === 8) {
    return `#${hex}`;
  }

  return null;
};

const rgbToHexAlpha = (value: string): string | null => {
  const trimmed = value.trim();
  const rgbaMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9]*\.?[0-9]+))?\s*\)$/i
  );

  if (!rgbaMatch) {
    return null;
  }

  const r = clamp(Number(rgbaMatch[1]), 0, 255);
  const g = clamp(Number(rgbaMatch[2]), 0, 255);
  const b = clamp(Number(rgbaMatch[3]), 0, 255);
  const alpha = rgbaMatch[4] !== undefined ? clamp(Number(rgbaMatch[4]), 0, 1) : 1;

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha * 255)}`;
};

const toPickerColour = (value?: string | null): string => {
  if (!value || value.trim().length === 0) {
    return DEFAULT_PICKER_COLOUR;
  }

  return normaliseHexColour(value) ?? rgbToHexAlpha(value) ?? DEFAULT_PICKER_COLOUR;
};

const ColourPicker = (props: IColourPickerProps) => {
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const inputGroupRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const selectedColour = isPropertyPicker(props)
    ? props.property.PublishedValue ?? ""
    : props.selectedColour ?? "";

  const pickerColour = useMemo(() => toPickerColour(selectedColour), [selectedColour]);

  const presetColours = useMemo(() => {
    if (!isPropertyPicker(props)) {
      return [];
    }

    return (props.property.DefaultValue ?? "")
      .split(",")
      .map((colour) => colour.trim())
      .filter((colour) => colour.length > 0);
  }, [props]);

  const emitInputChange = (value: string | null): void => {
    if (!("onInputChange" in props) || !props.onInputChange) {
      return;
    }

    const evt = {
      target: {
        value,
        name: props.name ?? "ColourPicker",
      },
      preventDefault: () => {},
      stopPropagation: () => {},
    };

    props.onInputChange(evt as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const emitColourChange = (value: string | null): void => {
    if (isPropertyPicker(props)) {
      props.onValueChange(props.property.Id, value ?? "");
      return;
    }

    props.onColourChange?.(value);
    emitInputChange(value);
  };

  const clearColour = (): void => {
    emitColourChange(null);
  };

  const handlePickerChange = (value: string): void => {
    emitColourChange(value);
  };

  useEffect(() => {
    const onDocumentPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      const clickedInsideField = pickerRef.current?.contains(target) ?? false;
      const clickedInsidePopover = popoverRef.current?.contains(target) ?? false;

      if (!clickedInsideField && !clickedInsidePopover) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentPointerDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePopoverPosition = (): void => {
      const rect = inputGroupRef.current?.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight ?? 280;
      const popoverWidth = popoverRef.current?.offsetWidth ?? 248;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const gap = 10;

      if (!rect) {
        return;
      }

      const spaceBelow = viewportHeight - rect.bottom;
      const shouldPlaceAbove = spaceBelow < popoverHeight + gap && rect.top > popoverHeight + gap;

      const top = shouldPlaceAbove
        ? Math.max(gap, rect.top - popoverHeight - gap)
        : rect.bottom + gap;

      const left = Math.min(
        Math.max(gap, rect.left),
        Math.max(gap, viewportWidth - popoverWidth - gap)
      );

      setPopoverPosition({
        top,
        left,
      });
    };

    updatePopoverPosition();
    const frame = window.requestAnimationFrame(updatePopoverPosition);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [isOpen]);

  return (
    <div
      className={`${styles.wrapper} ${isPropertyPicker(props) ? styles.propertyWrapper : ""}`.trim()}
      ref={pickerRef}>
      {isPropertyPicker(props) && (
        <FormLabel className={styles.propertyLabel}>{props.property.DisplayLabel}</FormLabel>
      )}

      <InputGroup ref={inputGroupRef} className={styles.inputGroup}>
        <button
          type="button"
          className={styles.swatchButton}
          aria-label="Open colour picker"
          onClick={() => setIsOpen((prev) => !prev)}>
          <span
            className={styles.swatchFill}
            style={
              selectedColour && selectedColour.length > 0
                ? {
                    backgroundColor: selectedColour,
                  }
                : undefined
            }
          />
        </button>

        <FormControl className={styles.hiddenNativeInput} tabIndex={-1} aria-hidden="true" />

        <FormControl
          name={isPropertyPicker(props) ? props.property.PropertyName : props.name ?? "ColourPicker"}
          id={isPropertyPicker(props) ? `property-colour-${props.property.Id}` : props.id ?? "ColourPicker"}
          className={styles.input}
          type="text"
          autoComplete="off"
          placeholder="#RRGGBB or #RRGGBBAA"
          value={selectedColour ?? ""}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onChange={(event) => handlePickerChange(event.target.value)}
        />

        <InputGroupText title="clear colour" onClick={clearColour} style={{ cursor: "pointer" }}>
          <FontAwesomeIcon icon={faTimes} />
        </InputGroupText>
      </InputGroup>

      {isOpen && popoverPosition && createPortal(
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }}>
          <HexAlphaColorPicker color={pickerColour} onChange={handlePickerChange} />

          {presetColours.length > 0 && (
            <div className={styles.presets}>
              {presetColours.map((colour) => (
                <button
                  key={colour}
                  type="button"
                  className={styles.presetButton}
                  title={colour}
                  onClick={() => handlePickerChange(colour)}>
                  <span className={styles.presetSwatch} style={{ backgroundColor: colour }} />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColourPicker;
